import { doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { logAdminConfigChange } from './adminAuditService';

/**
 * Settings → Form Configuration → Wizard layout: whether Recommendations and
 * Amendments use the "mirror" layout (outcome tiles as tabs, form beside a live
 * document preview) instead of the classic stacked sections.
 *
 *   app_settings/advice_wizard_layout = { mirror: { on, onlyFor }, scenarios: { on, onlyFor }, methodology: { on, onlyFor },
 *                                         uploadDocuments: { on, onlyFor } }
 *
 * `scenarios` (saved scenarios with one live, a tab strip above the tiles) only
 * applies to people who also have the mirror layout. `methodology` shows the
 * Methodology button (projection methodology + audit workbooks) on
 * Recommendations in either layout. `uploadDocuments` shows the Upload documents
 * button on the client (Oct 2026, document ingestion v1; the server checks it too,
 * server/lib/documentIngestion/routes.cjs).
 *
 * `on` turns it on for everyone; `onlyFor` turns it on for the listed people only
 * (a trial). OFF while loading and when the document is missing or unreadable, so
 * nobody sees the new layout until a super admin switches it on.
 */
const COLLECTION = 'app_settings';
const DOC_ID = 'advice_wizard_layout';

let value; // undefined while loading; null when missing or unreadable
let unsubscribe = null;
let retryTimer = null;
const listeners = new Set();

function notify() {
  listeners.forEach((fn) => fn(value));
}

function ensure() {
  if (unsubscribe) return;
  unsubscribe = onSnapshot(
    doc(db, COLLECTION, DOC_ID),
    (snap) => {
      value = snap.exists() ? snap.data() || null : null;
      notify();
    },
    (error) => {
      console.error('Wizard layout setting subscription error:', error);
      // A listener that errors (dropped connection, or started before sign-in
      // completed) is dead: clear it and try again while anyone is listening.
      unsubscribe = null;
      value = null;
      notify();
      if (listeners.size && !retryTimer) {
        retryTimer = setTimeout(() => {
          retryTimer = null;
          if (listeners.size) ensure();
        }, 5000);
      }
    },
  );
}

/** One shared live subscription; `fn(settings)` gets null when missing/unreadable. */
export function subscribeAdviceWizardLayout(fn) {
  listeners.add(fn);
  ensure();
  if (value !== undefined) fn(value);
  return () => {
    listeners.delete(fn);
    if (!listeners.size && unsubscribe) {
      unsubscribe();
      unsubscribe = null;
      value = undefined;
    }
  };
}

const normaliseEmail = (email) => String(email || '').trim().toLowerCase();

function resolveSwitch(entry, email) {
  if (!entry || typeof entry !== 'object') return false;
  if (entry.on === true) return true;
  const me = normaliseEmail(email);
  if (!me || !Array.isArray(entry.onlyFor)) return false;
  return entry.onlyFor.some((item) => normaliseEmail(item) === me);
}

/** Whether `email` sees the mirror layout under `settings` (false unless switched on). */
export function resolveAdviceWizardLayout(settings, email) {
  return resolveSwitch(settings && typeof settings === 'object' ? settings.mirror : null, email);
}

/** Whether `email` gets the scenario tab strip: its own switch, and the mirror layout too. */
export function resolveAdviceScenarios(settings, email) {
  if (!resolveAdviceWizardLayout(settings, email)) return false;
  return resolveSwitch(settings && typeof settings === 'object' ? settings.scenarios : null, email);
}

/** Whether `email` sees the Methodology button on Recommendations (either layout). */
export function resolveAdviceMethodology(settings, email) {
  return resolveSwitch(settings && typeof settings === 'object' ? settings.methodology : null, email);
}

/** Whether `email` sees Upload documents (Fact Find, Reverse Fact Find, Guided Advice, documents page). */
export function resolveUploadDocuments(settings, email) {
  return resolveSwitch(settings && typeof settings === 'object' ? settings.uploadDocuments : null, email);
}

/** The editable shape of one switch (`key`: 'mirror' | 'scenarios' | 'methodology' | 'uploadDocuments') for the settings form. */
export function readAdviceWizardLayoutForm(settings, email, key = 'mirror') {
  const entry = settings && settings[key] && typeof settings[key] === 'object' ? settings[key] : {};
  const me = normaliseEmail(email);
  const onlyFor = Array.isArray(entry.onlyFor) ? entry.onlyFor.map(normaliseEmail).filter(Boolean) : [];
  return { on: entry.on === true, onlyMe: Boolean(me && onlyFor.indexOf(me) !== -1), onlyFor };
}

const SWITCH_LABELS = { mirror: 'Mirror wizard layout', scenarios: 'Advice scenarios', methodology: 'Methodology button', uploadDocuments: 'Upload documents' };

/** Super admin only (firestore.rules: app_settings is written by super admins). `key`: 'mirror' | 'scenarios' | 'methodology' | 'uploadDocuments'. */
export async function saveAdviceWizardLayout({ on, onlyMe, onlyFor = [] }, email, key = 'mirror') {
  const me = normaliseEmail(email);
  const others = onlyFor.map(normaliseEmail).filter((entry) => entry && entry !== me);
  const nextOnlyFor = onlyMe && me ? others.concat(me) : others;
  const label = SWITCH_LABELS[key] || key;
  await setDoc(
    doc(db, COLLECTION, DOC_ID),
    { [key]: { on: on === true, onlyFor: nextOnlyFor }, updatedAt: serverTimestamp(), updatedBy: me || null },
    { merge: true },
  );
  logAdminConfigChange(
    'advice_wizard_layout',
    on ? `${label} on for everyone` : (nextOnlyFor.length ? `${label} on for ${nextOnlyFor.join(', ')}` : `${label} off`),
  );
  return { on: on === true, onlyMe: Boolean(onlyMe && me), onlyFor: nextOnlyFor };
}
